import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/button'
import FormField from '@/components/ui/FormField'

const numericField = z
  .string()
  .trim()
  .optional()
  .refine(
    (value) => !value || /^\d+$/.test(value),
    'Please enter a whole number',
  )

const formSchema = z.object({
  courseId: z.string().min(1, 'Select a course'),
  year: numericField,
  semester: numericField,
  required: z.boolean().default(true),
})

export type ProgrammeCurriculumFormValues = z.infer<typeof formSchema>

export interface ProgrammeCurriculumFormProps {
  mode: 'create' | 'edit'
  courses: Array<{ id: string; title: string }>
  defaultValues?: Partial<ProgrammeCurriculumFormValues>
  submitting?: boolean
  onSubmit: (values: ProgrammeCurriculumFormValues) => Promise<void> | void
  onCancel?: () => void
}

export function ProgrammeCurriculumForm({
  mode,
  courses,
  defaultValues,
  submitting = false,
  onSubmit,
  onCancel,
}: ProgrammeCurriculumFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProgrammeCurriculumFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      courseId: defaultValues?.courseId ?? '',
      year: defaultValues?.year ?? '',
      semester: defaultValues?.semester ?? '',
      required: defaultValues?.required ?? true,
    },
  })

  useEffect(() => {
    reset({
      courseId: defaultValues?.courseId ?? '',
      year: defaultValues?.year ?? '',
      semester: defaultValues?.semester ?? '',
      required: defaultValues?.required ?? true,
    })
  }, [defaultValues, reset])

  const handleFormSubmit = (values: ProgrammeCurriculumFormValues) => {
    onSubmit(values)
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit(handleFormSubmit)}>
      <FormField label="Course" required>
        <Select
          {...register('courseId')}
          className={errors.courseId ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
        >
          <option value="">Select course</option>
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.title}
            </option>
          ))}
        </Select>
        {errors.courseId && (
          <p className="text-xs text-red-500 mt-1">{errors.courseId.message}</p>
        )}
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Year">
          <Input
            type="number"
            placeholder="e.g. 1"
            {...register('year')}
            className={errors.year ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.year && (
            <p className="text-xs text-red-500 mt-1">{errors.year.message}</p>
          )}
        </FormField>

        <FormField label="Semester">
          <Input
            type="number"
            placeholder="e.g. 1"
            {...register('semester')}
            className={errors.semester ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}
          />
          {errors.semester && (
            <p className="text-xs text-red-500 mt-1">{errors.semester.message}</p>
          )}
        </FormField>
      </div>

      <div>
        <Checkbox
          label="Required course"
          {...register('required')}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : mode === 'create' ? 'Add Curriculum' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}
